import type { IpcCopyParams } from '@mimir/common/upload/fileCopyUpload';
import type { FileSetListener } from '@mimir/common/upload/uploadProgression';
import { IpcNodeApiModuleNames, type LimitedNodeApi } from '../nodejs/limitedNodeApi/nodeApi';
import type { LimitedFs } from '../nodejs/limitedNodeApi/fs';
import type { LimitedPath } from '../nodejs/limitedNodeApi/path';
import type { LimitedProcess } from '../nodejs/limitedNodeApi/process';
import type { LimitedZlib } from '../nodejs/limitedNodeApi/zlib';
import type { LimitedChildProcess } from '../nodejs/limitedNodeApi/childProcess';
import type { LocalDriveProvider } from '../types/driveInfo';
import type { IpcVfs } from '../types/ipcVfs';
import type { EncoderJobEvent } from '@mimir/primitive/nle/event';
import type { LogEntry } from '@mimir/primitive/structuredLogger';
import type { IpcLocalFfmpeg } from '../types/ipcLocalFfmpeg';

export interface IpcElectron {
  app: {
    getPath(name: 'home' | 'appData' | 'userData' | 'sessionData' | 'temp' | 'exe' | 'module' | 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos' | 'recent' | 'logs' | 'crashDumps'): string;
    getVersion(): string;
    exit(exitCode: number): void;
  };
  shell: {
    openPath(path: string): Promise<string>;
  };
  dialog: {
    showOpenDialog(options: { properties: Array<'openFile' | 'openDirectory' | 'multiSelections' | 'showHiddenFiles' | 'createDirectory' | 'promptToCreate' | 'noResolveAliases' | 'treatPackageAsDirectory' | 'dontAddToRecent'> }): Promise<{ filePaths: string[] }>;
  }
}
export interface IpcUtils {
  copyFileInMainProcess: (params: IpcCopyParams, listener: FileSetListener) => Promise<{ wasCancelled: boolean }>;
  cancelCopyFileInMainProcess: (executionId: string) => void;
  startDrag: (filePaths: string[], iconDataUrl: string | undefined) => void;
  restart: () => void;
  pinWindow: (pin: boolean) => void;
  openDevTools: () => void;
}
export interface IpcDvr {
  cleanup: () => void;
  isLaunchedFromDvr: () => boolean;
  callDvrAction: (action: string, args: any[]) => Promise<any>;
}
export interface DvrActionReply {
  result?: unknown;
  error?: { name: string; message: string; stack?: string };
}
export interface IpcDvrActionsModule {
  isLaunchedFromDvr: () => boolean;
  getDvrVersion: () => Promise<string | null>;
  callDvrAction: (action: string, args: unknown[]) => Promise<DvrActionReply>;
}
export interface IpcRendererLogger {
  error: (...params: any[]) => void;
  warn: (...params: any[]) => void;
  info: (...params: any[]) => void;
  debug: (...params: any[]) => void;
  log: (...params: any[]) => void;
}
export interface IpcOs {
  tmpdir(): string;
  platform(): string;
}

const DesktopMainProcessModuleNames = [
  ...IpcNodeApiModuleNames,
  'child_process',
  'os',
  'electron',
  './utils',
  './dvr',
  './dvr/actions',
  './rendererLogger',
] as const;

export type DesktopMainProcessModuleNamesType = typeof DesktopMainProcessModuleNames[number];

export type DesktopMainProcessModuleType<T extends DesktopMainProcessModuleNamesType> =
  T extends 'fs' ? LimitedFs :
  T extends 'path' ? LimitedPath :
  T extends 'child_process' ? LimitedChildProcess :
  T extends 'os' ? IpcOs :
  T extends 'process' ? LimitedProcess :
  T extends 'zlib' ? LimitedZlib :
  T extends 'electron' ? IpcElectron :
  T extends './utils' ? IpcUtils :
  T extends './dvr' ? IpcDvr :
  T extends './dvr/actions' ? IpcDvrActionsModule :
  IpcRendererLogger;

export type DesktopMainProcessRequireType = <T extends DesktopMainProcessModuleNamesType>(type: T) => DesktopMainProcessModuleType<T>;

export class LazyRequireLimitedNodeApi implements LimitedNodeApi {
  constructor(public require: DesktopMainProcessRequireType) {}
  get fs(): LimitedFs { return this.require('fs'); }
  get path(): LimitedPath { return this.require('path'); }
  get process(): LimitedProcess { return this.require('process'); }
  get zlib(): LimitedZlib { return this.require('zlib'); }
}

export function getIpcNodeApi(requireFunction: DesktopMainProcessRequireType): LimitedNodeApi {
  return new LazyRequireLimitedNodeApi(requireFunction);
}

export interface MimirElectronContextBridgeObject {
  ipcVfs: IpcVfs;
  ipcDriveProvider: LocalDriveProvider;
  rendererRequire: typeof require;
  mainRequire: DesktopMainProcessRequireType;
  onRefreshClipInfos: (callback: () => void) => () => void;
  onRenderEvent: (callback: (event: EncoderJobEvent) => void) => () => void;
  onLogEvent: (callback: (event: LogEntry) => void) => () => void;
  onDvrWorkflowIntegrationLaunched: (callback: (exited: boolean) => void) => void;
  getPathForFile: (file: File) => string;
  ipcLocalFfmpeg?: IpcLocalFfmpeg;
  postLocalFfmpegPort?: (requestId: number) => void;
  ipcRenderer?: any;   // Remove when we no longer need to support <2.0.0 versions of Mimir Desktop
}
